"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Slide from "@mui/material/Slide";
import { AlertColor, SnackbarOrigin, AlertProps } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";

type Notification = {
  message: string;
  severity: AlertColor;
  autoHideDuration: number;
  anchorOrigin: SnackbarOrigin;
  variant: AlertProps["variant"];
  elevation: number;
  action?: React.ReactNode;
  TransitionComponent?: React.ElementType;
};

type NotificationsContextType = {
  show: (message: string, options?: Partial<Omit<Notification, "message">>) => void;
};

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) throw new Error("useNotifications must be used within NotificationsProvider");
  return context;
};

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [open, setOpen] = useState(false);

  const DEFAULT_OPTIONS: Omit<Notification, "message"> = {
    severity: "success",
    autoHideDuration: 2500,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
    variant: "standard",
    elevation: 6,
    TransitionComponent: Slide,
  };

  const show = useCallback((message: string, options?: Partial<Omit<Notification, "message">>) => {
    setNotification({ message, ...DEFAULT_OPTIONS, ...options });
    setOpen(true);
  }, []);

  const handleClose = () => setOpen(false);

  return (
    <NotificationsContext.Provider value={{ show }}>
      {children}
      <Snackbar
        open={open}
        onClose={handleClose}
        autoHideDuration={notification?.autoHideDuration}
        anchorOrigin={notification?.anchorOrigin}
        action={notification?.action}
        slots={{ transition: notification?.TransitionComponent }}
        sx={notification?.anchorOrigin?.vertical === "bottom" ? { bottom: 25 } : undefined}
      >
        <MuiAlert
          onClose={handleClose}
          severity={notification?.severity}
          variant={notification?.variant}
          elevation={notification?.elevation}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <WarningIcon fontSize="inherit" />,
            info: <InfoIcon fontSize="inherit" />,
          }}
        >
          {notification?.message}
        </MuiAlert>
      </Snackbar>
    </NotificationsContext.Provider>
  );
};
